import { FUND_EXIT_ENTERPRISE_ALIAS, FUND_EXIT_EQUITY_ALIAS } from 'common/constants/allocations';
import { getEnterpriseValueFormula, getEquityValueFormula } from 'common/formulas/math.js/ENTERPRISE_AND_EQUITY_VALUE';
import { AfterCellChanged } from 'common/types/scalarSpreadsheet/scalarSpreadsheet';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  FOREIGN_EXCHANGE_RATE_BASE_ALIAS,
  FOREIGN_EXCHANGE_RATE_TARGET_ALIAS,
  FOREIGN_LESS_DEBT_ALIAS,
  FOREIGN_PLUS_CASH_ALIAS,
  LESS_DEBT_ALIAS,
  PLUS_CASH_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import {
  EXCHANGE_RATE_ALIAS,
  EXCHANGE_RATE_REFERENCE,
  EXCHANGE_RATE_SHEET_NAME,
} from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/ExchangeRateSheet/ExchangeRateSheet.constants';

export const inputSheetAfterCellsChanged: AfterCellChanged = (initialChanges, cells, _rowConfig, _tableData, scope) => {
  if (initialChanges.length === 0) {
    throw new Error('No changes detected');
  }

  const change = initialChanges[0];
  const { cell } = change;
  const changes = [...initialChanges];
  const findCell = (alias: string) => Object.values(cells).find(candidate => candidate.alias === alias);
  const exchangeRateCell = scope?.[EXCHANGE_RATE_SHEET_NAME]?.[EXCHANGE_RATE_ALIAS] as EvaluatedCell | undefined;
  const exchangeRate = Number(exchangeRateCell?.value);
  const hasValidExchangeRate = Number.isFinite(exchangeRate) && exchangeRate > 0;

  const updateEnterpriseValue = (equityValueCell: EvaluatedCell) => {
    const cashCell = findCell(PLUS_CASH_ALIAS);
    const debtCell = findCell(LESS_DEBT_ALIAS);
    const enterpriseValueCell = findCell(EXIT_ENTERPRISE_VALUE_ALIAS);
    if (enterpriseValueCell && cashCell && debtCell) {
      const expr = getEnterpriseValueFormula(equityValueCell.key, cashCell.key, debtCell.key);
      changes.unshift({ cell: enterpriseValueCell, value: `=${expr}` });
    }
  };

  const updateEquityValue = (enterpriseValueCell: EvaluatedCell) => {
    const cashCell = findCell(PLUS_CASH_ALIAS);
    const debtCell = findCell(LESS_DEBT_ALIAS);
    const equityValueCell = findCell(EXIT_EQUITY_ALIAS);
    if (equityValueCell && cashCell && debtCell) {
      const expr = getEquityValueFormula(enterpriseValueCell.key, cashCell.key, debtCell.key);
      changes.unshift({ cell: equityValueCell, value: `=${expr}` });
    }
  };

  const updateBaseValueWithRate = (baseCell: EvaluatedCell | undefined) => {
    if (baseCell && hasValidExchangeRate) {
      changes.unshift({ cell: baseCell, value: `=${cell.key} / ${EXCHANGE_RATE_REFERENCE}` });
    }
  };

  const updateForeignValueWithRate = (foreignCell: EvaluatedCell | undefined) => {
    if (foreignCell && hasValidExchangeRate) {
      changes.unshift({ cell: foreignCell, value: `=${cell.key} * ${EXCHANGE_RATE_REFERENCE}` });
    }
  };

  switch (cell.alias) {
    case EXIT_EQUITY_ALIAS:
      updateEnterpriseValue(cell);
      updateForeignValueWithRate(findCell(FUND_EXIT_EQUITY_ALIAS));
      break;
    case EXIT_ENTERPRISE_VALUE_ALIAS:
      updateEquityValue(cell);
      updateForeignValueWithRate(findCell(FUND_EXIT_ENTERPRISE_ALIAS));
      break;
    case FUND_EXIT_EQUITY_ALIAS: {
      const equityValueCell = findCell(EXIT_EQUITY_ALIAS);
      updateBaseValueWithRate(equityValueCell);
      if (equityValueCell) {
        updateEnterpriseValue(equityValueCell);
      }
      break;
    }
    case FUND_EXIT_ENTERPRISE_ALIAS: {
      const enterpriseValueCell = findCell(EXIT_ENTERPRISE_VALUE_ALIAS);
      updateBaseValueWithRate(enterpriseValueCell);
      if (enterpriseValueCell) {
        updateEquityValue(enterpriseValueCell);
      }
      break;
    }
    case PLUS_CASH_ALIAS:
      updateForeignValueWithRate(findCell(FOREIGN_PLUS_CASH_ALIAS));
      break;
    case LESS_DEBT_ALIAS:
      updateForeignValueWithRate(findCell(FOREIGN_LESS_DEBT_ALIAS));
      break;
    case FOREIGN_PLUS_CASH_ALIAS:
      updateBaseValueWithRate(findCell(PLUS_CASH_ALIAS));
      break;
    case FOREIGN_LESS_DEBT_ALIAS:
      updateBaseValueWithRate(findCell(LESS_DEBT_ALIAS));
      break;
    case FOREIGN_EXCHANGE_RATE_BASE_ALIAS: {
      const foreignExchangeRateCell = findCell(FOREIGN_EXCHANGE_RATE_TARGET_ALIAS);
      const rate = Number(change.value);
      if (foreignExchangeRateCell && exchangeRateCell && Number.isFinite(rate) && rate > 0) {
        changes.unshift({ cell: foreignExchangeRateCell, value: 1 });
        changes.unshift({ cell: exchangeRateCell, value: 1 / rate });
      }
      break;
    }
    case FOREIGN_EXCHANGE_RATE_TARGET_ALIAS: {
      const baseExchangeRateCell = findCell(FOREIGN_EXCHANGE_RATE_BASE_ALIAS);
      const rate = Number(change.value);
      if (baseExchangeRateCell && exchangeRateCell && Number.isFinite(rate) && rate > 0) {
        changes.unshift({ cell: baseExchangeRateCell, value: 1 });
        changes.unshift({ cell: exchangeRateCell, value: rate });
      }
      break;
    }
    default:
      break;
  }

  return changes;
};
